import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Grid, Card, CardContent,
  Chip, Collapse, IconButton, InputAdornment, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Timeline as PathwayIcon,
  CollectionsBookmark as CollectionsIcon,
  CalendarToday as DateIcon,
  Person as UserIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import FlexHeaderContainer from '../../../../components/containers/FlexHeaderContainer';
import StandardErrorAlert from '../../../../components/alerts/StandardErrorAlert';
import CenteredFlexContainer from '../../../../components/containers/CenteredFlexContainer';
import MediumCircularProgress from '../../../../components/progressBar/MediumCircularProgress';
import InfiniteScroll from '../../../../components/common/InfiniteScroll';
import { useNavigate } from 'react-router-dom';
import CreatePathwayOrCollection from '../CreatePathwayOrCollection';
import { searchPathways, deletePathway } from '../../../../services/practice-collection-service';
import { format } from 'date-fns';
import PathwayDetailsDialog from './PathwayDetailsDialog';

const ListPracticePathway = () => {
  const navigate = useNavigate();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pathways, setPathways] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [editingPathway, setEditingPathway] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pathwayToDelete, setPathwayToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const pageSize = 10;

  const fetchPathways = useCallback(async (pageNum, query = '', reset = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await searchPathways({
        term: query,
        pageNumber: pageNum,
        pageSize
      });
      
      const newItems = result.items || [];
      const hasNext = result.hasNext || false;

      setPathways(prev => reset ? newItems : [...prev, ...newItems]);
      setPage(pageNum);
      setHasMore(hasNext);
    } catch (err) {
      console.error('Failed to fetch pathways:', err);
      setError(err.message || 'Failed to fetch pathways');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch on load
  useEffect(() => {
    fetchPathways(0, '', true);
  }, [fetchPathways]);

  const handleSearchSubmit = (event) => {
    if (event) event.preventDefault();
    setSearchTerm(localSearchTerm);
    fetchPathways(0, localSearchTerm, true);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    setSearchTerm('');
    fetchPathways(0, '', true);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchPathways(page + 1, searchTerm, false);
    }
  };

  const handlePathwayCreated = () => {
    setShowCreateForm(false);
    setEditingPathway(null);
    fetchPathways(0, searchTerm, true);
  };

  const handleOpenDeleteConfirm = (pathway) => {
    setPathwayToDelete(pathway);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setPathwayToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!pathwayToDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await deletePathway(pathwayToDelete.id);
      setDeleteConfirmOpen(false);
      setPathwayToDelete(null);
      fetchPathways(0, searchTerm, true);
    } catch (err) {
      console.error('Failed to delete pathway:', err);
      setError(err.message || 'Failed to delete pathway');
    } finally {
      setDeleting(false);
    }
  };

  const formatDateString = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <FlexHeaderContainer>
        <Typography variant="h4" component="h1" color="primary" fontWeight="bold">
          Practice Pathways
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm(true)}
        >
          Create Pathway
        </Button>
      </FlexHeaderContainer>

      {error && <StandardErrorAlert>{error}</StandardErrorAlert>}

      <Collapse in={showCreateForm || Boolean(editingPathway)} mountOnEnter unmountOnExit sx={{ mb: 3 }}>
        <CreatePathwayOrCollection
          pathway={editingPathway}
          onCancel={() => {
            setShowCreateForm(false);
            setEditingPathway(null);
          }}
          onContentGenerated={handlePathwayCreated}
        />
      </Collapse>

      {/* Search Bar */}
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: 'flex', gap: 1, mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search pathways by name or code..."
          value={localSearchTerm}
          onChange={(e) => setLocalSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: localSearchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Button type="submit" variant="contained" color="primary" sx={{ px: 4 }}>
          Search
        </Button>
      </Box>

      {/* Pathways List */}
      <InfiniteScroll
        loading={loading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
      >
        {pathways.length === 0 && !loading ? (
          <CenteredFlexContainer sx={{ py: 8, flexDirection: 'column', gap: 2 }}>
            <PathwayIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">
              No Practice Pathways found
            </Typography>
            {searchTerm && (
              <Button variant="outlined" onClick={handleClearSearch}>
                Clear Search
              </Button>
            )}
          </CenteredFlexContainer>
        ) : (
          <Grid container spacing={3}>
            {pathways.map((pathway) => (
              <Grid item xs={12} key={pathway.id}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '&:hover': { 
                      transform: 'translateY(-3px)',
                      boxShadow: 3,
                      borderColor: 'primary.main'
                    } 
                  }}
                  onClick={() => setSelectedPathway(pathway)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" component="div">
                          {pathway.name}
                        </Typography>
                        {pathway.description && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                            {pathway.description}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {pathway.status === 'DRAFT' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingPathway(pathway);
                                setShowCreateForm(false);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                              sx={{ mr: 1 }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteConfirm(pathway);
                              }}
                              sx={{ mr: 1 }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                        <Chip
                          label={pathway.code}
                          color="primary"
                          variant="outlined"
                          size="small"
                          fontWeight="bold"
                        />
                        <Chip
                          label={pathway.status}
                          color={pathway.status === 'ACTIVE' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="600" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CollectionsIcon fontSize="small" />
                        Collections ({pathway.collections.length})
                      </Typography>
                      {pathway.collections.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ ml: 3 }}>
                          No collections added to this pathway yet.
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 3 }}>
                          {pathway.collections.map((colId) => (
                            <Chip
                              key={colId}
                              icon={<CollectionsIcon />}
                              label={`ID: ${colId.substring(0, 8)}...`}
                              size="small"
                              variant="outlined"
                              clickable
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/practice-collections/${colId}`);
                              }}
                              sx={{ bgcolor: 'action.hover' }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DateIcon sx={{ fontSize: 14 }} />
                        <span>Created: {formatDateString(pathway.createdAt)}</span>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <UserIcon sx={{ fontSize: 14 }} />
                        <span>By: {pathway.createdBy}</span>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {loading && pathways.length > 0 && (
          <CenteredFlexContainer sx={{ py: 4 }}>
            <MediumCircularProgress />
          </CenteredFlexContainer>
        )}
      </InfiniteScroll>

      {/* Details Dialog */}
      <PathwayDetailsDialog
        pathway={selectedPathway}
        open={Boolean(selectedPathway)}
        onClose={() => setSelectedPathway(null)}
        onEdit={(pathwayToEdit) => {
          setSelectedPathway(null);
          setEditingPathway(pathwayToEdit);
          setShowCreateForm(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onDelete={(pathwayToDelete) => {
          setSelectedPathway(null);
          handleOpenDeleteConfirm(pathwayToDelete);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={deleting ? null : handleCloseDeleteConfirm}
        onClick={(e) => e.stopPropagation()}
        aria-labelledby="delete-pathway-dialog-title"
        aria-describedby="delete-pathway-dialog-description"
      >
        <DialogTitle id="delete-pathway-dialog-title">
          Delete Practice Pathway
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-pathway-dialog-description">
            Are you sure you want to delete the practice pathway "{pathwayToDelete?.name}"? This action is permanent and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={deleting} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} disabled={deleting} color="error" variant="contained" autoFocus>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListPracticePathway;
